/**
 * Topic Posts List
 *
 * Displays posts for a topic with add/edit/delete functionality.
 * Includes AI generation capability.
 */

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TopicPostDialog } from './TopicPostDialog';
import {
  useDeleteTopicPost,
  useGenerateTopicPost,
  type TopicPost,
} from '@/hooks/queries/useTopicPosts';
import { Plus, Pencil, Trash2, FileText, Sparkles, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

interface TopicPostsListProps {
  childId: string;
  childAge?: number;
  topicId?: string;
  customTopicId?: string;
  topicName: string;
  topicDescription?: string;
  posts: TopicPost[];
}

export function TopicPostsList({
  childId,
  childAge,
  topicId,
  customTopicId,
  topicName,
  topicDescription,
  posts,
}: TopicPostsListProps) {
  const deletePost = useDeleteTopicPost();
  const generatePost = useGenerateTopicPost();

  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<TopicPost | null>(null);
  const [deletingPost, setDeletingPost] = useState<TopicPost | null>(null);
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());

  const togglePost = (postId: string) => {
    setExpandedPosts(prev => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };
  const [generatedContent, setGeneratedContent] = useState<{
    title: string;
    content: string;
  } | null>(null);

  const handleAddPost = () => {
    setEditingPost(null);
    setGeneratedContent(null);
    setPostDialogOpen(true);
  };

  const handleGenerateWithAI = async () => {
    try {
      const result = await generatePost.mutateAsync({
        topicName,
        childAge,
      });
      setGeneratedContent(result);
      setEditingPost(null);
      setPostDialogOpen(true);
    } catch {
      // Error handled by mutation
    }
  };

  const handleEditPost = (post: TopicPost) => {
    setEditingPost(post);
    setGeneratedContent(null);
    setPostDialogOpen(true);
  };

  const handleDeletePost = async () => {
    if (!deletingPost) return;

    try {
      await deletePost.mutateAsync({
        childId,
        postId: deletingPost.id,
      });
      setDeletingPost(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleDialogClose = (open: boolean) => {
    setPostDialogOpen(open);
    if (!open) {
      setGeneratedContent(null);
    }
  };

  return (
    <div className="space-y-3 pt-2">
      {/* Default content from topic description */}
      {topicDescription && (
        <div className="flex items-start gap-3 rounded-lg border p-3 bg-primary/5 border-primary/20">
          <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-body-sm text-primary">About {topicName}</p>
            <p className="text-caption text-muted-foreground mt-1">
              {topicDescription}
            </p>
          </div>
        </div>
      )}

      {/* User-added posts */}
      {posts.length > 0 && (
        <div className="space-y-2">
          {posts.map((post) => (
            <div
              key={post.id}
              className="rounded-lg border p-3 bg-muted/30"
            >
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={() => togglePost(post.id)}
                  className="flex-1 min-w-0 text-left flex items-center gap-2"
                >
                  {expandedPosts.has(post.id) ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <p className="font-medium text-body-sm">{post.title}</p>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleEditPost(post)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => setDeletingPost(post)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              </div>
              {expandedPosts.has(post.id) && (
                <div className="mt-3 pt-3 border-t text-caption text-muted-foreground prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-strong:text-foreground">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-center gap-2 px-1 mt-1">
        <button
          onClick={handleGenerateWithAI}
          disabled={generatePost.isPending}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] transition hover:bg-[#E8F6F4]"
          style={{ fontWeight: 600, color: '#3ECDC6', border: '1px solid #3ECDC6' }}
        >
          {generatePost.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {generatePost.isPending ? 'Generating...' : 'Generate with AI'}
        </button>
        <button
          onClick={handleAddPost}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] transition hover:bg-[#E8F6F4]"
          style={{ fontWeight: 600, color: '#3ECDC6', border: '1px solid #3ECDC6' }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add Content
        </button>
      </div>

      {/* Post dialog */}
      <TopicPostDialog
        open={postDialogOpen}
        onOpenChange={handleDialogClose}
        childId={childId}
        topicId={topicId}
        customTopicId={customTopicId}
        topicName={topicName}
        post={editingPost}
        initialContent={generatedContent}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deletingPost}
        onOpenChange={(open) => !open && setDeletingPost(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingPost?.title}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePost}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
